import React, { useEffect, useState } from 'react';
import { InputText } from 'primereact/inputtext';
import { PasswordInput } from '../../shared/components/common/password-input/PasswordInput';
import { Button } from 'primereact/button';
import './ResetPasswordPage.scss';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { AuthData } from '../login/models/LoginFormData';
import { FORGOT, LOGIN } from '../../shared/routes/path';
import Loader from '../../shared/components/common/loader/Loader';
import { HashErrorType } from '../../shared/types/reset-password/HashErrorType';
import {
  apiErrorToText,
  getTitleByHashStatus,
} from '../../shared/helpers/reset-password/ResetPasswordHelper';
import { validateHash, reset } from '../../shared/services/reset-password/ResetPasswordService';

const ResetPasswordPage: React.FC = () => {
  const navigate = useNavigate();
  const { hash } = useParams();
  const { t } = useTranslation();
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm();

  const [apiError, setApiError] = useState<string | null>(null);
  const [hashStatus, setHashStatus] = useState<HashErrorType | null>(null);

  useEffect(() => {
    document.body.classList.add('visible', 'white');

    if (hash) {
      checkHash(hash);
    } else {
      setHashStatus('VALID');
    }
  }, []);

  const checkHash = async (hash: string) => {
    await validateHash(hash)
      .then((data) => setHashStatus(data.hashStatus))
      .catch(() => setHashStatus('INVALID'));
  };

  const onSubmit = async (formData: any) => {
    if (!hash) {
      setApiError(t('wrongHash'));
      return;
    }

    const data = {
      ...formData,
    };

    const urlEncodedData = new URLSearchParams();
    for (const key in data) {
      if (data.hasOwnProperty(key)) {
        urlEncodedData.append(key, data[key as keyof AuthData]);
      }
    }

    await reset(hash, urlEncodedData)
      .then(() => navigate(LOGIN))
      .catch((err) => setApiError(err.response.data.error));
  };

  const clearApiError = () => {
    setApiError(null);
  };

  if (hashStatus === null) {
    return <Loader />;
  }

  const { title, subtitle, btnLabel } = getTitleByHashStatus(hashStatus);
  return (
    <div className="reset-page auth-pages">
      <div className="auth-pages__left">
        <div className="auth-pages__image">
          <div className="auth-pages__logo">
            <a href="https://chatbeat.com">
              <div className="logo-box" />
            </a>
          </div>
          <div className="auth-pages__desc" dangerouslySetInnerHTML={{ __html: t('almostThere') }} />
        </div>
      </div>
      <div className="auth-pages__right">
        <div className="auth-pages__belt">
          <a href="https://chatbeat.com">
            <div className="logo-box logo-box--second" />
          </a>
        </div>
        <div className="form-box form-box--auth">
          <div className="form-box__title">{title}</div>
          <div className="form-box__row form-box__row--login">{subtitle}</div>
          {hashStatus !== 'VALID' && btnLabel && (
            <Button className="btn btn--primary" label={btnLabel} onClick={() => navigate(FORGOT)} />
          )}

          {hashStatus === 'VALID' && (
            <form onSubmit={handleSubmit(onSubmit)} className="form-box__form">
              <div className="form-box__row">
                <div className="form-box__label">{t('password')}:</div>
                <PasswordInput
                  {...register('password', { required: t('fieldIsRequired') })}
                  className={errors.password || apiError ? 'form-box__invalid' : ''}
                  onInput={clearApiError}
                />
                {errors.password && <span>{errors.password.message as string}</span>}
              </div>

              <div className="form-box__row">
                <div className="form-box__label">{t('rePassword')}:</div>
                <PasswordInput
                  {...register('rePassword', { required: t('fieldIsRequired') })}
                  className={errors.rePassword || apiError ? 'form-box__invalid' : ''}
                  onInput={clearApiError}
                />
                {errors.rePassword && <span>{errors.rePassword.message as string}</span>}
                {apiError && <span dangerouslySetInnerHTML={{ __html: t(apiErrorToText(apiError)) }} />}
              </div>

              <Button className="btn" label={t('continue')} />
            </form>
          )}
        </div>
        <div className="auth-pages__links links">
          <Link to="https://chatbeat.com/privacy-policy">{t('privacyPolicy')}</Link>
          <span className="links__dot" />
          <Link to="https://chatbeat.com/terms-of-use">{t('termsAndConditions')}</Link>
        </div>
      </div>
    </div>
  );
};

export default ResetPasswordPage;
